import React, { useEffect, useId, useState } from "react";
import styled from "styled-components";
import FormGroup from "./FormGroup";
import FormInputGroup from "./FormInputGroup";
import FormLabel from "./FormLabel";
import InputErrorMessage from "./InputErrorMessage";
import { InputSpinnerMd } from "../loaders/InputSpinners";
import { Form, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ThemedIcon } from "../utilities";

export const EyeButton = styled(ThemedIcon)`
    cursor: pointer;
`;

export const StyledFormInput = styled(Form.Control)`
    && {
        ${props => props.loading === 'true' ? InputSpinnerMd : ''}
    }
`;

const PasswordInput = ({
    autoComplete = "off",
    disableAnimations = false,
    disabled,
    horizontal = false,
    id,
    label,
    onBlur,
    onChange,
    size = "md",
    successTimeout = 1000,
    value,
    errorMessage = null,
    errorAllowRetry = true,
    ...rest
}) => {
    const defaultComponentId = useId();
    const componentId = id || defaultComponentId;
    const [type, setType] = useState('password');
    const [icon, setIcon] = useState("fa-eye-slash");
    const [error, setError] = useState(errorMessage);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentValue, setCurrentValue] = useState(value || '');
    const [originalValue, setOriginalValue] = useState(value || '');

    const onBlurEvent = (e) => {
        // check if we have a prevent default method we need to stop
        if (e && typeof (e.preventDefault) === 'function') {
            e.preventDefault();
        }

        // check that the value has changed before triggering anything
        // NOTE: also check the function HAS been passed into this component
        if (currentValue === originalValue || !onBlur || typeof (onBlur) !== 'function') {
            return;
        }

        // trigger the loading animation on the input
        if (disableAnimations !== true) {
            setError(_ => null);
            setIsLoading(_ => true);
        }

        // used to safely check the function returns something we can check on
        var possibleResult = onBlur(currentValue, e);

        // we can now safely use the `then` method and handle the response accordingly
        Promise.resolve(possibleResult).then(
            _ => {
                setOriginalValue(currentValue);
                
                if (disableAnimations === true) {
                    return;
                }
                setIsLoading(_ => false);
                setIsSuccess(_ => true);
            },
            error => {
                if (disableAnimations === true) {
                    return;
                }
                setIsLoading(_ => false);
                setError(_ => error);
            }
        );
    };

    const onChangeEvent = (e) => {
        setCurrentValue(e.target.value);
        if (onChange && typeof (onChange) === 'function') {
            onChange(e);
        }
    };

    const onShowPassword = () => {
        if (type === 'password') {
            setType('text');
            setIcon('fa-eye');
        }
        else {
            setType('password');
            setIcon('fa-eye-slash');
        }
    };

    useEffect(() => {
        if (errorMessage) {
            setError(_ => errorMessage);
        }
        else {
            setError(_ => null);
        }
    }, [errorMessage]);

    useEffect(() => {
        if (isSuccess === false) {
            return;
        }

        let t = setTimeout(() => {
            setIsSuccess(_ => false);
        }, successTimeout);

        return () => clearTimeout(t);
    }, [isSuccess, successTimeout]);

    useEffect(() => {
        if (value != null && value !== currentValue) {
            setCurrentValue(value);
            setOriginalValue(value)
        }
    }, [value]);

    return <FormGroup horizontal={horizontal}>
        {label && <FormLabel htmlFor={componentId} horizontal={horizontal}>{label}</FormLabel>}
        <FormInputGroup className="has-validation" horizontal={horizontal} hasLabel={label ? true : false}>
            <StyledFormInput
                id={componentId}
                type={type}
                size={size}
                value={currentValue}
                loading={isLoading.toString()}
                disabled={disabled || isLoading}
                isInvalid={error !== null}
                isValid={isSuccess}
                onBlur={onBlurEvent}
                onChange={onChangeEvent}
                autoComplete={autoComplete}
                {...rest}
            />
            <InputGroup.Text className={`px-2 bg-transparent`} onClick={onShowPassword} >
                <EyeButton 
                    icon={icon} 
                    variant="muted"
                    disabled={true} 
                />
            </InputGroup.Text>
            <InputErrorMessage error={error} retryCallback={onBlurEvent} allowRetry={errorAllowRetry} />
        </FormInputGroup>
    </FormGroup>
};

export default PasswordInput;